import { apiClient, unwrap } from '@/lib/apiClient';
import type { ApiSuccess, AuthPayload, AuthUser } from '@/types/api';
import type { LoginFormValues } from '../schemas/login.schema';
import type { MyAccountPayload } from '@/features/institute/account/schemas/myAccount.schema';

/**
 * Every network call the auth feature makes. Components never talk to
 * `apiClient` directly — they go through a function here, so an endpoint
 * change touches one file.
 */
export const authApi = {
  login: (credentials: LoginFormValues) =>
    unwrap(apiClient.post<ApiSuccess<AuthPayload>>('/auth/login', credentials)),

  /**
   * Uses the httpOnly cookie — no argument needed from the browser. Resolves
   * to null when there is no cookie: a visitor who never signed in.
   */
  refresh: () => unwrap(apiClient.post<ApiSuccess<AuthPayload | null>>('/auth/refresh', {})),

  logout: () => unwrap(apiClient.post<ApiSuccess<null>>('/auth/logout', {})),

  logoutAll: () => unwrap(apiClient.post<ApiSuccess<null>>('/auth/logout-all', {})),

  me: () => unwrap(apiClient.get<ApiSuccess<{ user: AuthUser }>>('/auth/me')),

  /**
   * An administrator saving their own name, email and mobile. A new email or
   * mobile needs `currentPassword`. Answers with the updated account.
   */
  updateMyAccount: (payload: MyAccountPayload) =>
    unwrap(apiClient.patch<ApiSuccess<{ user: AuthUser }>>('/auth/me', payload)),

  changePassword: (payload: { currentPassword: string; newPassword: string; confirmPassword: string }) =>
    unwrap(apiClient.patch<ApiSuccess<null>>('/auth/password', payload)),

  /**
   * Asks for a reset link. Always succeeds, whether or not the account exists —
   * the server deliberately gives nothing away, so the screen must not either.
   */
  forgotPassword: (identifier: string) =>
    unwrap(apiClient.post<ApiSuccess<null>>('/auth/forgot-password', { identifier })),

  /** Used by both a forgotten password and a new administrator's invite link. */
  resetPassword: (payload: { token: string; newPassword: string; confirmPassword: string }) =>
    unwrap(apiClient.post<ApiSuccess<null>>('/auth/reset-password', payload)),
};
